import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Camera,
  CheckCircle2,
  ClipboardList,
  Clock,
  Hammer,
  ImagePlus,
  Link2,
  MessageSquarePlus,
  Package,
  Pencil,
  Ship,
  ShieldCheck,
  ShieldX,
  Tag,
  Trash2,
  Truck,
  Undo2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Skeleton } from '@/components/ui/Skeleton';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { Textarea } from '@/components/ui/Textarea';
import { Fact, StoredImage, WorkerChip } from '@/components/production/shared';
import { WorkPhotoCapture } from '@/components/work/WorkPhotoCapture';
import { useAuth } from '@/app/AuthProvider';
import { compressImage } from '@/features/workMode/images';
import { describeWorkError, formatDuration } from '@/features/workMode/api';
import type { WorkLookups } from '@/features/workMode/useLookups';
import {
  useAddUnitNote,
  useAddUnitPhotos,
  useDeleteUnit,
  useQcUnit,
  useSerializedUnitDetail,
  useSerializedUnitRow,
  useSetUnitLots,
  useSetUnitStatus,
  uploadUnitPhoto,
} from '@/features/traceability/hooks';
import {
  UNIT_STATUS_LABEL,
  UNIT_STATUS_VARIANT,
  unitDestination,
  type ComponentLot,
  type QcResult,
  type SerializedUnitEvent,
  type SerializedUnitRow,
  type SerializedUnitStatus,
} from '@/features/traceability/types';
import { formatDateTime, formatRelative } from '@/lib/formatters';
import { ShipUnitsDialog } from './ShipUnitsDialog';

interface UnitSheetProps {
  unitId: string | null;
  lookups: WorkLookups;
  canManage: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The whole life of one unit: photos, build, QC, lots, where it went, timeline. */
export function UnitSheet({ unitId, lookups, canManage, onOpenChange }: UnitSheetProps) {
  return (
    <Sheet open={!!unitId} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-2xl">
        {unitId && <UnitDetail key={unitId} unitId={unitId} lookups={lookups} canManage={canManage} onClose={() => onOpenChange(false)} />}
      </SheetContent>
    </Sheet>
  );
}

function UnitDetail({ unitId, lookups, canManage, onClose }: { unitId: string; lookups: WorkLookups; canManage: boolean; onClose: () => void }) {
  const { data: row, isLoading } = useSerializedUnitRow(unitId);
  const { data: detail } = useSerializedUnitDetail(unitId);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [action, setAction] = useState<null | 'qc' | 'photos' | 'lots' | 'note' | 'status' | 'ship' | 'delete'>(null);
  const [statusTarget, setStatusTarget] = useState<Exclude<SerializedUnitStatus, 'shipped'>>('on_hold');

  if (isLoading || !row) {
    return (
      <>
        <SheetHeader>
          <SheetTitle>Unit</SheetTitle>
          <SheetDescription>Loading…</SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-3">
          <Skeleton className="h-40 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
        </SheetBody>
      </>
    );
  }

  const u = row;
  const photos = [u.label_photo_path, ...(u.photo_paths ?? [])].filter((p): p is string => !!p);
  const where = unitDestination(u);
  const events = detail?.events ?? [];

  const openStatus = (s: Exclude<SerializedUnitStatus, 'shipped'>) => {
    setStatusTarget(s);
    setAction('status');
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/mfg/production/units?serial=${encodeURIComponent(u.serial_number)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied.');
    } catch {
      toast.message(url);
    }
  };

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex flex-wrap items-center gap-2">
          <Package className="h-5 w-5 text-bright-moss-dark" />
          <span className="font-mono">{u.serial_number}</span>
          <Badge variant={UNIT_STATUS_VARIANT[u.status]}>{UNIT_STATUS_LABEL[u.status]}</Badge>
          {u.qc_result === 'fail' && <Badge variant="warning-soft">QC fail</Badge>}
        </SheetTitle>
        <SheetDescription>
          {u.items ? (
            <Link to={`/catalog/items/${u.item_id}`} className="hover:underline">
              {u.items.name} · <span className="font-mono text-xs">{u.items.sku}</span>
            </Link>
          ) : (
            'Unknown item'
          )}
        </SheetDescription>
      </SheetHeader>

      <SheetBody className="space-y-6">
        <section>
          {photos.length === 0 ? (
            <div className="flex items-center justify-between rounded-xl border border-dashed border-border px-4 py-5 text-sm text-muted-foreground">
              <span>No photos on file for this unit.</span>
              <Button size="sm" variant="outline" onClick={() => setAction('photos')}>
                <ImagePlus className="h-4 w-4" />
                Add photos
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {photos.map((p, i) => (
                <div key={p} className="relative">
                  <StoredImage bucket="work-captures" path={p} alt={i === 0 && u.label_photo_path ? 'Serial label' : `Unit photo ${i + 1}`} className="aspect-square w-full" onClick={() => setLightbox(p)} />
                  {i === 0 && u.label_photo_path && (
                    <span className="pointer-events-none absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-humus/80 px-2 py-0.5 text-[10px] font-medium text-white">
                      <Tag className="h-3 w-3" />
                      Label
                    </span>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setAction('photos')}
                className="flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border text-muted-foreground hover:bg-secondary"
              >
                <ImagePlus className="h-5 w-5" />
                <span className="text-xs">Add</span>
              </button>
            </div>
          )}
        </section>

        <section className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-3 rounded-xl border border-border p-4">
            <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <Hammer className="h-3.5 w-3.5" />
              Built
            </p>
            <WorkerChip person={lookups.person(u.built_by)} sub={formatDateTime(u.built_at)} size={36} />
            <div className="grid grid-cols-2 gap-3">
              <Fact label="Station">{u.workstations?.name ?? lookups.stationName(u.workstation_id) ?? '—'}</Fact>
              <Fact label="Build time">{u.build_seconds != null ? formatDuration(u.build_seconds) : '—'}</Fact>
            </div>
            {u.notes && <p className="whitespace-pre-line text-sm text-foreground">{u.notes}</p>}
          </div>
          <div className="space-y-3 rounded-xl border border-border p-4">
            <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" />
              Quality check
            </p>
            {u.qc_result ? (
              <>
                <WorkerChip person={lookups.person(u.qc_by)} sub={u.qc_at ? formatDateTime(u.qc_at) : undefined} size={36} />
                <div className="flex items-center gap-2">
                  {u.qc_result === 'pass' ? (
                    <Badge variant="success-soft">
                      <CheckCircle2 className="mr-1 h-3 w-3" />
                      Passed
                    </Badge>
                  ) : (
                    <Badge variant="warning-soft">
                      <XCircle className="mr-1 h-3 w-3" />
                      Failed
                    </Badge>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setAction('qc')}>
                    Inspect again
                  </Button>
                </div>
                {u.qc_notes && <p className="whitespace-pre-line text-sm text-foreground">{u.qc_notes}</p>}
              </>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">Not inspected yet.</p>
                <Button size="sm" variant="outline" onClick={() => setAction('qc')}>
                  Record QC
                </Button>
              </div>
            )}
          </div>
        </section>

        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <ClipboardList className="h-3.5 w-3.5" />
              Component lots
            </p>
            <Button size="sm" variant="ghost" onClick={() => setAction('lots')}>
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Button>
          </div>
          {(u.component_lots ?? []).length === 0 ? (
            <p className="rounded-xl border border-dashed border-border px-4 py-4 text-sm text-muted-foreground">No component lots recorded for this unit.</p>
          ) : (
            <ul className="divide-y divide-border rounded-xl border border-border">
              {u.component_lots.map((l, i) => (
                <li key={`${l.sku}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span className="min-w-0 truncate text-foreground">
                    {l.name || l.sku || 'Component'} {l.sku && l.name ? <span className="font-mono text-xs text-muted-foreground">{l.sku}</span> : null}
                  </span>
                  <span className="flex shrink-0 items-center gap-2 font-mono text-xs">
                    {l.lot_number}
                    {!l.lot_id && <span className="rounded-full bg-secondary px-1.5 text-[10px] font-sans text-muted-foreground" title="No matching lot in inventory">unmatched</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="space-y-3 rounded-xl border border-border p-4">
          <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <Truck className="h-3.5 w-3.5" />
            Where it went
          </p>
          {u.status === 'shipped' || u.status === 'returned' || where ? (
            <div className="grid grid-cols-2 gap-3">
              <Fact label="Customer">{u.customers?.name ?? '—'}</Fact>
              <Fact label="Destination">{u.destination ?? where ?? '—'}</Fact>
              <Fact label="Sales order">{u.sales_orders?.so_number ?? '—'}</Fact>
              <Fact label="Shipment">
                {u.shipments ? (
                  <>
                    {u.shipments.shipment_number}
                    {u.shipments.tracking_number ? <span className="text-muted-foreground"> · {u.shipments.tracking_number}</span> : null}
                  </>
                ) : (
                  '—'
                )}
              </Fact>
              <Fact label="Shipped">{u.shipped_at ? formatDateTime(u.shipped_at) : '—'}</Fact>
              <Fact label="Shipped by">{u.shipped_by ? lookups.personName(u.shipped_by) : '—'}</Fact>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {u.status === 'scrapped' ? 'Scrapped — never shipped.' : u.status === 'on_hold' ? 'On hold. Release it before it can ship.' : 'Still here. Not shipped yet.'}
            </p>
          )}
          {canManage && (
            <div className="flex flex-wrap gap-2 pt-1">
              {u.status === 'built' && (
                <Button size="sm" onClick={() => setAction('ship')}>
                  <Ship className="h-4 w-4" />
                  Ship this unit
                </Button>
              )}
              {u.status === 'shipped' && (
                <>
                  <Button size="sm" variant="outline" onClick={() => openStatus('returned')}>
                    <Undo2 className="h-4 w-4" />
                    Mark returned
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openStatus('built')}>
                    Un-ship
                  </Button>
                </>
              )}
            </div>
          )}
        </section>

        {canManage && (
          <section className="flex flex-wrap gap-2">
            {u.status === 'built' && (
              <Button size="sm" variant="outline" onClick={() => openStatus('on_hold')}>
                Put on hold
              </Button>
            )}
            {u.status === 'on_hold' && (
              <Button size="sm" variant="outline" onClick={() => openStatus('built')}>
                Release hold
              </Button>
            )}
            {u.status === 'returned' && (
              <Button size="sm" variant="outline" onClick={() => openStatus('built')}>
                Back in stock
              </Button>
            )}
            {u.status !== 'scrapped' && u.status !== 'shipped' && (
              <Button size="sm" variant="outline" className="border-fresh-salmon/70 text-fresh-salmon-dark hover:bg-fresh-salmon/15" onClick={() => openStatus('scrapped')}>
                <Trash2 className="h-4 w-4" />
                Scrap
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setAction('note')}>
              <MessageSquarePlus className="h-4 w-4" />
              Add note
            </Button>
            <Button size="sm" variant="ghost" onClick={copyLink}>
              <Link2 className="h-4 w-4" />
              Copy link
            </Button>
            <Button size="sm" variant="ghost" className="ml-auto text-muted-foreground" onClick={() => setAction('delete')}>
              Delete
            </Button>
          </section>
        )}
        {!canManage && (
          <section className="flex flex-wrap gap-2">
            <Button size="sm" variant="ghost" onClick={() => setAction('note')}>
              <MessageSquarePlus className="h-4 w-4" />
              Add note
            </Button>
          </section>
        )}

        <section className="space-y-2">
          <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            Timeline
          </p>
          <Timeline events={events} lookups={lookups} />
        </section>
      </SheetBody>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl p-2">
          <DialogTitle className="sr-only">Photo</DialogTitle>
          {lightbox && <StoredImage bucket="work-captures" path={lightbox} alt="Unit photo" className="max-h-[80vh] w-full object-contain" />}
        </DialogContent>
      </Dialog>

      <QcDialog unit={u} open={action === 'qc'} onOpenChange={(o) => !o && setAction(null)} />
      <AddPhotosDialog unit={u} open={action === 'photos'} onOpenChange={(o) => !o && setAction(null)} />
      <LotsDialog unit={u} open={action === 'lots'} onOpenChange={(o) => !o && setAction(null)} />
      <NoteDialog unit={u} open={action === 'note'} onOpenChange={(o) => !o && setAction(null)} />
      <StatusDialog unit={u} target={statusTarget} open={action === 'status'} onOpenChange={(o) => !o && setAction(null)} />
      <DeleteDialog unit={u} open={action === 'delete'} onOpenChange={(o) => !o && setAction(null)} onDeleted={onClose} />
      <ShipUnitsDialog open={action === 'ship'} onOpenChange={(o) => !o && setAction(null)} initialSerials={[u.serial_number]} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Timeline
// ---------------------------------------------------------------------------

const EVENT_LABEL: Record<string, string> = {
  built: 'Built and logged at the bench',
  registered: 'Registered by hand',
  qc: 'Quality check',
  status: 'Status changed',
  photos: 'Photos added',
  lots: 'Component lots updated',
  note: 'Note',
  shipped: 'Shipped',
  returned: 'Returned',
};

function eventSummary(e: SerializedUnitEvent, lookups: WorkLookups): string | null {
  const d = e.data ?? {};
  switch (e.kind) {
    case 'qc':
      return `${d.result === 'pass' ? 'Passed' : 'Failed'}${d.at_build ? ' at the bench' : ''}`;
    case 'status':
      return `${UNIT_STATUS_LABEL[d.from as SerializedUnitStatus] ?? String(d.from)} → ${UNIT_STATUS_LABEL[d.to as SerializedUnitStatus] ?? String(d.to)}`;
    case 'photos':
      return `${Number(d.added) || 0} photo${Number(d.added) === 1 ? '' : 's'}`;
    case 'lots': {
      const after = Array.isArray(d.after) ? (d.after as ComponentLot[]) : [];
      return after.length ? after.map((l) => `${l.sku || l.name}: ${l.lot_number}`).join(', ') : 'Cleared';
    }
    case 'built': {
      const bits = [
        d.workstation_id ? lookups.stationName(String(d.workstation_id)) : null,
        d.build_seconds != null ? formatDuration(Number(d.build_seconds)) : null,
        d.photos != null ? `${d.photos} photo${Number(d.photos) === 1 ? '' : 's'}` : null,
        d.lots != null ? `${d.lots} lot${Number(d.lots) === 1 ? '' : 's'}` : null,
      ].filter(Boolean);
      return bits.join(' · ') || null;
    }
    case 'shipped': {
      const bits = [d.destination ? String(d.destination) : null].filter(Boolean);
      return bits.join(' · ') || null;
    }
    default:
      return null;
  }
}

function Timeline({ events, lookups }: { events: SerializedUnitEvent[]; lookups: WorkLookups }) {
  if (events.length === 0) return <p className="text-sm text-muted-foreground">No events yet.</p>;
  const ordered = [...events].reverse();
  return (
    <ol className="relative space-y-4 border-l border-border pl-5">
      {ordered.map((e) => {
        const summary = eventSummary(e, lookups);
        const tone = e.kind === 'shipped' ? 'bg-atmosphere-dark' : e.kind === 'qc' && e.data?.result === 'fail' ? 'bg-fresh-salmon-dark' : e.kind === 'status' && e.data?.to === 'scrapped' ? 'bg-fresh-salmon-dark' : 'bg-bright-moss-dark';
        return (
          <li key={e.id} className="relative">
            <span className={cn('absolute -left-[26px] top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-card', tone)} />
            <p className="text-sm font-medium text-foreground">{EVENT_LABEL[e.kind] ?? e.kind}</p>
            {summary && <p className="text-sm text-muted-foreground">{summary}</p>}
            {e.note && <p className="mt-0.5 whitespace-pre-line text-sm text-foreground">{e.note}</p>}
            <p className="mt-0.5 text-xs text-muted-foreground">
              {lookups.personName(e.actor_id)} · {formatDateTime(e.created_at)} ({formatRelative(e.created_at)})
            </p>
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Action dialogs
// ---------------------------------------------------------------------------

function QcDialog({ unit, open, onOpenChange }: { unit: SerializedUnitRow; open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQcUnit();
  const [result, setResult] = useState<QcResult>('pass');
  const [notes, setNotes] = useState('');
  useEffect(() => {
    if (open) {
      setResult('pass');
      setNotes('');
    }
  }, [open]);
  const save = () => {
    if (result === 'fail' && !notes.trim()) {
      toast.error('Say what failed so it can be fixed.');
      return;
    }
    qc.mutate(
      { id: unit.id, result, notes: notes.trim() || null },
      {
        onSuccess: () => {
          toast.success(result === 'pass' ? 'QC pass recorded.' : 'QC fail recorded — the unit is on hold.');
          onOpenChange(false);
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !qc.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle>Quality check · {unit.serial_number}</DialogTitle>
          <DialogDescription>Recorded under your name with the time.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setResult('pass')}
            className={cn(
              'flex min-h-[56px] items-center justify-center gap-2 rounded-xl border text-sm font-medium',
              result === 'pass' ? 'border-bright-moss-dark bg-bright-moss/20 text-bright-moss-dark' : 'border-border hover:bg-secondary',
            )}
          >
            <ShieldCheck className="h-4 w-4" /> Pass
          </button>
          <button
            type="button"
            onClick={() => setResult('fail')}
            className={cn(
              'flex min-h-[56px] items-center justify-center gap-2 rounded-xl border text-sm font-medium',
              result === 'fail' ? 'border-fresh-salmon-dark bg-fresh-salmon/25 text-fresh-salmon-dark' : 'border-border hover:bg-secondary',
            )}
          >
            <ShieldX className="h-4 w-4" /> Fail
          </button>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="qc-notes">Notes{result === 'fail' ? '' : ' (optional)'}</Label>
          <Textarea id="qc-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder={result === 'fail' ? 'What failed?' : 'Anything worth noting.'} />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={qc.isPending}>
            Cancel
          </Button>
          <Button onClick={save} disabled={qc.isPending}>
            {qc.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddPhotosDialog({ unit, open, onOpenChange }: { unit: SerializedUnitRow; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { profile } = useAuth();
  const add = useAddUnitPhotos();
  const [files, setFiles] = useState<File[]>([]);
  const [asLabel, setAsLabel] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setFiles([]);
      setAsLabel(false);
    }
  }, [open]);
  const save = async () => {
    if (!profile?.organization_id || files.length === 0) return;
    setBusy(true);
    try {
      const paths: string[] = [];
      for (const f of files) paths.push(await uploadUnitPhoto(profile.organization_id, unit.serial_number, await compressImage(f), f.name.replace(/\.[^.]+$/, '') + '.jpg'));
      const labelPath = asLabel && !unit.label_photo_path ? paths.shift() ?? null : null;
      await add.mutateAsync({ id: unit.id, paths, labelPhotoPath: labelPath });
      toast.success('Photos added.');
      onOpenChange(false);
    } catch (e) {
      toast.error(describeWorkError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Add photos · {unit.serial_number}
          </DialogTitle>
        </DialogHeader>
        <WorkPhotoCapture files={files} onChange={setFiles} max={8} disabled={busy} />
        {!unit.label_photo_path && files.length > 0 && (
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={asLabel} onChange={(e) => setAsLabel(e.target.checked)} className="h-4 w-4 rounded border-border" />
            The first photo is the serial label close-up
          </label>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy || files.length === 0}>
            {busy ? 'Uploading…' : `Add ${files.length || ''} photo${files.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LotsDialog({ unit, open, onOpenChange }: { unit: SerializedUnitRow; open: boolean; onOpenChange: (o: boolean) => void }) {
  const set = useSetUnitLots();
  const [rows, setRows] = useState<ComponentLot[]>([]);
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) {
      setRows(unit.component_lots?.length ? unit.component_lots.map((l) => ({ ...l })) : [{ item_id: null, sku: '', name: '', lot_number: '', lot_id: null }]);
      setNote('');
    }
  }, [open, unit.component_lots]);
  const update = (i: number, patch: Partial<ComponentLot>) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const save = () => {
    set.mutate(
      { id: unit.id, lots: rows.filter((r) => r.lot_number.trim()), note: note.trim() || null },
      {
        onSuccess: () => {
          toast.success('Component lots saved.');
          onOpenChange(false);
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !set.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-2xl rounded-2xl">
        <DialogHeader>
          <DialogTitle>Component lots · {unit.serial_number}</DialogTitle>
          <DialogDescription>Which lot of each component is inside this unit. Lot numbers that match inventory are linked automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
              <Input value={r.sku} onChange={(e) => update(i, { sku: e.target.value })} placeholder="SKU" className="font-mono" disabled={!!r.item_id} />
              <Input value={r.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="Component" disabled={!!r.item_id} />
              <Input value={r.lot_number} onChange={(e) => update(i, { lot_number: e.target.value })} placeholder="Lot number" className="font-mono" />
              <Button variant="ghost" size="icon" aria-label="Remove row" onClick={() => setRows((rows) => rows.filter((_, idx) => idx !== i))}>
                <XCircle className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setRows((r) => [...r, { item_id: null, sku: '', name: '', lot_number: '', lot_id: null }])}>
            Add a component
          </Button>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lots-note">Why the change (optional)</Label>
          <Input id="lots-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. worker wrote the lot on the traveller" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={set.isPending}>
            Cancel
          </Button>
          <Button onClick={save} disabled={set.isPending}>
            {set.isPending ? 'Saving…' : 'Save lots'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NoteDialog({ unit, open, onOpenChange }: { unit: SerializedUnitRow; open: boolean; onOpenChange: (o: boolean) => void }) {
  const add = useAddUnitNote();
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) setNote('');
  }, [open]);
  const save = () => {
    if (!note.trim()) return;
    add.mutate(
      { id: unit.id, note: note.trim() },
      {
        onSuccess: () => {
          toast.success('Note added.');
          onOpenChange(false);
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !add.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle>Note · {unit.serial_number}</DialogTitle>
        </DialogHeader>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="Goes on the unit's timeline under your name." autoFocus />
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={add.isPending}>
            Cancel
          </Button>
          <Button onClick={save} disabled={add.isPending || !note.trim()}>
            {add.isPending ? 'Saving…' : 'Add note'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_COPY: Record<Exclude<SerializedUnitStatus, 'shipped'>, { title: string; body: string; cta: string; destructive?: boolean }> = {
  on_hold: { title: 'Put on hold', body: 'The unit stays in the log but cannot be shipped until it is released.', cta: 'Put on hold' },
  built: { title: 'Back to built', body: 'The unit is available again. If it was shipped, the sale is cleared from the record (the timeline keeps it).', cta: 'Confirm' },
  scrapped: { title: 'Scrap this unit', body: 'Marks the unit as scrapped. The record and photos are kept for traceability.', cta: 'Scrap', destructive: true },
  returned: { title: 'Mark returned', body: 'The customer sent this unit back. Where it was sold stays on the record.', cta: 'Mark returned' },
};

function StatusDialog({ unit, target, open, onOpenChange }: { unit: SerializedUnitRow; target: Exclude<SerializedUnitStatus, 'shipped'>; open: boolean; onOpenChange: (o: boolean) => void }) {
  const set = useSetUnitStatus();
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) setNote('');
  }, [open]);
  const copy = STATUS_COPY[target];
  const save = () => {
    set.mutate(
      { id: unit.id, status: target, note: note.trim() || null },
      {
        onSuccess: () => {
          toast.success(`${unit.serial_number} is now ${UNIT_STATUS_LABEL[target].toLowerCase()}.`);
          onOpenChange(false);
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !set.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle>
            {copy.title} · {unit.serial_number}
          </DialogTitle>
          <DialogDescription>{copy.body}</DialogDescription>
        </DialogHeader>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Reason (optional, goes on the timeline)" />
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={set.isPending}>
            Cancel
          </Button>
          <Button variant={copy.destructive ? 'destructive' : 'default'} onClick={save} disabled={set.isPending}>
            {set.isPending ? 'Saving…' : copy.cta}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ unit, open, onOpenChange, onDeleted }: { unit: SerializedUnitRow; open: boolean; onOpenChange: (o: boolean) => void; onDeleted: () => void }) {
  const del = useDeleteUnit();
  const [confirm, setConfirm] = useState('');
  useEffect(() => {
    if (open) setConfirm('');
  }, [open]);
  const ok = confirm.trim().toUpperCase() === unit.serial_number.toUpperCase();
  return (
    <Dialog open={open} onOpenChange={(o) => !del.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle>Delete {unit.serial_number}?</DialogTitle>
          <DialogDescription>
            Only for a test scan or a duplicate logged by mistake. The unit, its timeline and its link to the work unit are removed; a real unit should be scrapped instead so the history stays.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="del-confirm">Type the serial number to confirm</Label>
          <Input id="del-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="font-mono" autoComplete="off" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={del.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!ok || del.isPending}
            onClick={() =>
              del.mutate(
                { id: unit.id },
                {
                  onSuccess: () => {
                    toast.success('Unit deleted.');
                    onOpenChange(false);
                    onDeleted();
                  },
                  onError: (e) => toast.error(describeWorkError(e)),
                },
              )
            }
          >
            {del.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
